/**
 * Scheduler de descarga por serie (prioridad activa) + tracker de progreso img/s · MiB/s.
 * Extraído del path R2 para reutilizar en legacy WADO y Orthanc.
 */

/**
 * @param {Array<{ seriesIdx: number }>} tasks
 * @returns {Map<number, number[]>}
 */
export function buildSeriesSlots(tasks) {
  const seriesSlots = new Map()
  if (!Array.isArray(tasks)) return seriesSlots
  tasks.forEach((t, slot) => {
    if (!t || typeof t.seriesIdx !== 'number') return
    if (!seriesSlots.has(t.seriesIdx)) seriesSlots.set(t.seriesIdx, [])
    seriesSlots.get(t.seriesIdx).push(slot)
  })
  return seriesSlots
}

/**
 * @param {Array<{ totalTasks?: number }>} seriesInfo
 * @returns {number[]}
 */
function buildThumbSeriesOrder(seriesInfo, numSeries) {
  return [...Array(numSeries).keys()].sort((a, b) => {
    const ta = (seriesInfo[a] && seriesInfo[a].totalTasks) || 0
    const tb = (seriesInfo[b] && seriesInfo[b].totalTasks) || 0
    if (ta !== tb) return ta - tb
    return a - b
  })
}

/**
 * Scheduler mixto:
 * - 1ª de la activa: siempre primero (viewport).
 * - Hasta `sideBudget` slots en vuelo a otras series (thumbs / BG).
 * - Hasta `activeCap` slots en vuelo a la serie en foco.
 *
 * @param {{
 *   numSeries: number,
 *   seriesSlots: Map<number, number[]>,
 *   seriesInfo: Array<{ totalTasks?: number }>,
 *   parallel: number,
 *   getActiveSeriesIdx?: (() => number)|null,
 *   getPinnedSeriesIdx?: (() => number[])|null,
 *   getScrollFocus?: (() => { seriesIdx?: number, sliceIndex?: number, radius?: number }|null)|null,
 * }} opts
 */
export function createSeriesPriorityScheduler(opts) {
  const numSeries = Math.max(0, Number(opts.numSeries) || 0)
  const seriesSlots = opts.seriesSlots || new Map()
  const seriesInfo = Array.isArray(opts.seriesInfo) ? opts.seriesInfo : []
  const parallel = Math.max(1, Number(opts.parallel) || 1)
  const getActiveSeriesIdx =
    typeof opts.getActiveSeriesIdx === 'function'
      ? opts.getActiveSeriesIdx
      : () => 0
  const getPinnedSeriesIdx =
    typeof opts.getPinnedSeriesIdx === 'function'
      ? opts.getPinnedSeriesIdx
      : () => []
  const getScrollFocus =
    typeof opts.getScrollFocus === 'function' ? opts.getScrollFocus : null

  const completed = new Set()
  const inFlight = new Set()
  const slotSeriesIdx = new Map()

  /** Focus P0 explícito (opcional; si no, se lee getScrollFocus en cada pick). */
  let scrollFocusOverride = null

  let maxDepth = 0
  for (const [sIdx, arr] of seriesSlots.entries()) {
    maxDepth = Math.max(maxDepth, arr.length)
    for (const slot of arr) slotSeriesIdx.set(slot, sIdx)
  }

  const thumbSeriesOrder = buildThumbSeriesOrder(seriesInfo, numSeries)
  // sideBudget: slots para series no-activas (thumbs + fondo).
  // Con parallel=6: 2 slots side. Con parallel=12: 4 slots side.
  // Cine XA: cap bajo (opts.maxSideBudget) para no decodificar N Part-10 en paralelo.
  const sideBudgetRaw = Math.max(2, Math.min(Math.floor(parallel * 0.4), 6))
  const sideBudget =
    opts.maxSideBudget != null
      ? Math.max(1, Math.min(sideBudgetRaw, Number(opts.maxSideBudget) || 1))
      : sideBudgetRaw
  const activeCapDefault = Math.max(1, parallel - sideBudget)

  const totalSlots = (() => {
    let n = 0
    for (const arr of seriesSlots.values()) n += arr.length
    return n
  })()

  function resolveActiveSeriesIdx() {
    try {
      const v = getActiveSeriesIdx()
      if (typeof v === 'number' && !Number.isNaN(v)) return v
    } catch (e) {
      /* ignore */
    }
    return 0
  }

  function resolvePinnedSeriesIdx(activeIdx) {
    let raw = []
    try {
      raw = getPinnedSeriesIdx()
    } catch (e) {
      raw = []
    }
    if (!Array.isArray(raw) || raw.length === 0) return []
    const seen = new Set([activeIdx])
    const out = []
    for (const v of raw) {
      const n = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : NaN
      if (!Number.isFinite(n) || n < 0 || n >= numSeries) continue
      if (seen.has(n)) continue
      seen.add(n)
      out.push(n)
    }
    return out
  }

  function claimPinnedFirsts(activeIdx, pinned) {
    for (const s of pinned) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s)
      if (!slots || slots.length === 0) continue
      if (tryClaimSlot(slots[0])) return slots[0]
    }
    return null
  }

  function claimPinnedRest(activeIdx, pinned) {
    for (const s of pinned) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s) || []
      for (let i = 1; i < slots.length; i++) {
        if (tryClaimSlot(slots[i])) return slots[i]
      }
    }
    return null
  }

  function hasOpenPinnedFirst(activeIdx, pinned) {
    for (const s of pinned) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s)
      if (slots && slots.length > 0 && isSlotOpen(slots[0])) return true
    }
    return false
  }

  function hasOpenPinnedRest(activeIdx, pinned) {
    for (const s of pinned) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s) || []
      for (let i = 1; i < slots.length; i++) {
        if (isSlotOpen(slots[i])) return true
      }
    }
    return false
  }

  function backgroundSeriesOrder(activeIdx) {
    return [...Array(numSeries).keys()]
      .filter((s) => s !== activeIdx)
      .sort((a, b) => {
        const ta = (seriesInfo[a] && seriesInfo[a].totalTasks) || 0
        const tb = (seriesInfo[b] && seriesInfo[b].totalTasks) || 0
        if (ta !== tb) return ta - tb
        return a - b
      })
  }

  function isSlotOpen(slot) {
    return !completed.has(slot) && !inFlight.has(slot)
  }

  function countInFlightForSeries(seriesIdx) {
    let n = 0
    for (const slot of inFlight) {
      if (slotSeriesIdx.get(slot) === seriesIdx) n += 1
    }
    return n
  }

  function tryClaimSlot(slot) {
    if (completed.has(slot) || inFlight.has(slot)) return false
    inFlight.add(slot)
    return true
  }

  /**
   * P0: corte visible + vecinos (±radius) de la serie en foco.
   * Preempt del gesto de scroll sin pausar el fetch de fondo.
   */
  function resolveScrollFocus(activeIdx) {
    let raw = scrollFocusOverride
    if (!raw && getScrollFocus) {
      try {
        raw = getScrollFocus()
      } catch (e) {
        raw = null
      }
    }
    if (!raw || typeof raw !== 'object') {
      return { seriesIdx: activeIdx, sliceIndex: 0, radius: 2 }
    }
    const seriesIdx =
      typeof raw.seriesIdx === 'number' && !Number.isNaN(raw.seriesIdx)
        ? Math.max(0, Math.min(numSeries - 1, raw.seriesIdx))
        : activeIdx
    const sliceIndex = Math.max(0, Math.floor(Number(raw.sliceIndex) || 0))
    const radius = Math.max(
      0,
      Math.min(8, Math.floor(raw.radius != null ? Number(raw.radius) : 2))
    )
    return { seriesIdx, sliceIndex, radius }
  }

  function claimScrollFocusP0(activeIdx) {
    const focus = resolveScrollFocus(activeIdx)
    const slots = seriesSlots.get(focus.seriesIdx) || []
    if (!slots.length) return null
    const center = Math.min(slots.length - 1, focus.sliceIndex)
    if (tryClaimSlot(slots[center])) return slots[center]
    for (let d = 1; d <= focus.radius; d++) {
      const hi = center + d
      const lo = center - d
      if (hi < slots.length && tryClaimSlot(slots[hi])) return slots[hi]
      if (lo >= 0 && tryClaimSlot(slots[lo])) return slots[lo]
    }
    return null
  }

  function setScrollFocus(seriesIdx, sliceIndex, radius) {
    scrollFocusOverride = {
      seriesIdx: Math.max(0, Math.floor(Number(seriesIdx) || 0)),
      sliceIndex: Math.max(0, Math.floor(Number(sliceIndex) || 0)),
      radius:
        radius != null
          ? Math.max(0, Math.min(8, Math.floor(Number(radius) || 2)))
          : 2,
    }
  }

  function clearScrollFocus() {
    scrollFocusOverride = null
  }

  function claimActiveFirst(activeIdx) {
    const activeSlots = seriesSlots.get(activeIdx) || []
    if (activeSlots.length > 0 && tryClaimSlot(activeSlots[0])) {
      return activeSlots[0]
    }
    return null
  }

  function claimActiveRest(activeIdx) {
    const activeSlots = seriesSlots.get(activeIdx) || []
    for (let i = 1; i < activeSlots.length; i++) {
      if (tryClaimSlot(activeSlots[i])) return activeSlots[i]
    }
    if (activeSlots.length > 0 && tryClaimSlot(activeSlots[0])) {
      return activeSlots[0]
    }
    return null
  }

  function claimOtherFirsts(activeIdx) {
    for (const s of thumbSeriesOrder) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s)
      if (!slots || slots.length === 0) continue
      if (tryClaimSlot(slots[0])) return slots[0]
    }
    return null
  }

  function claimBackground(activeIdx) {
    const bgOrder = backgroundSeriesOrder(activeIdx)
    for (let d = 1; d < maxDepth; d++) {
      for (const s of bgOrder) {
        const slots = seriesSlots.get(s)
        if (!slots || d >= slots.length) continue
        if (tryClaimSlot(slots[d])) return slots[d]
      }
    }
    return null
  }

  function hasOpenOtherFirst(activeIdx) {
    for (const s of thumbSeriesOrder) {
      if (s === activeIdx) continue
      const slots = seriesSlots.get(s)
      if (slots && slots.length > 0 && isSlotOpen(slots[0])) return true
    }
    return false
  }

  function hasOpenBackground(activeIdx) {
    const bgOrder = backgroundSeriesOrder(activeIdx)
    for (let d = 1; d < maxDepth; d++) {
      for (const s of bgOrder) {
        const slots = seriesSlots.get(s)
        if (!slots || d >= slots.length) continue
        if (isSlotOpen(slots[d])) return true
      }
    }
    return false
  }

  function hasOpenActive(activeIdx) {
    const activeSlots = seriesSlots.get(activeIdx) || []
    return activeSlots.some((slot) => isSlotOpen(slot))
  }

  function pickNextSlot() {
    if (numSeries <= 0) return null
    const rawActive = resolveActiveSeriesIdx()
    const activeIdx = Math.max(0, Math.min(numSeries - 1, rawActive))
    const pinned = resolvePinnedSeriesIdx(activeIdx)

    const sideWork =
      pinned.length > 0 ||
      hasOpenOtherFirst(activeIdx) ||
      hasOpenBackground(activeIdx)
    const activeCap = sideWork ? activeCapDefault : parallel

    const activeIF = countInFlightForSeries(activeIdx)
    const sideIF = Math.max(0, inFlight.size - activeIF)

    // P0: slice visible + vecinos (gesto) — antes que thumbs/fondo
    let slot = claimScrollFocusP0(activeIdx)
    if (slot != null) return slot

    slot = claimActiveFirst(activeIdx)
    if (slot != null) return slot

    if (activeIF < activeCap && hasOpenActive(activeIdx)) {
      slot = claimActiveRest(activeIdx)
      if (slot != null) return slot
    }

    if (pinned.length > 0 && hasOpenPinnedFirst(activeIdx, pinned)) {
      slot = claimPinnedFirsts(activeIdx, pinned)
      if (slot != null) return slot
    }

    if (sideIF < sideBudget && hasOpenOtherFirst(activeIdx)) {
      slot = claimOtherFirsts(activeIdx)
      if (slot != null) return slot
    }

    if (pinned.length > 0 && hasOpenPinnedRest(activeIdx, pinned)) {
      slot = claimPinnedRest(activeIdx, pinned)
      if (slot != null) return slot
    }

    if (activeIF < activeCap && hasOpenActive(activeIdx)) {
      slot = claimActiveRest(activeIdx)
      if (slot != null) return slot
    }

    if (hasOpenOtherFirst(activeIdx)) {
      slot = claimOtherFirsts(activeIdx)
      if (slot != null) return slot
    }

    if (
      hasOpenBackground(activeIdx) &&
      (sideIF < sideBudget || !hasOpenActive(activeIdx) || activeIF >= activeCap)
    ) {
      slot = claimBackground(activeIdx)
      if (slot != null) return slot
    }

    slot = claimActiveRest(activeIdx)
    if (slot != null) return slot

    if (pinned.length > 0) {
      slot = claimPinnedRest(activeIdx, pinned)
      if (slot != null) return slot
    }

    slot = claimBackground(activeIdx)
    if (slot != null) return slot

    return null
  }

  function finishSlot(slot) {
    inFlight.delete(slot)
    completed.add(slot)
  }

  function releaseInFlight(slot) {
    inFlight.delete(slot)
  }

  return {
    pickNextSlot,
    finishSlot,
    releaseInFlight,
    tryClaimSlot,
    isSlotOpen,
    setScrollFocus,
    clearScrollFocus,
    completed,
    inFlight,
    sideBudget,
    activeCapDefault,
    totalSlots,
    isDone: () => completed.size >= totalSlots && totalSlots > 0,
  }
}

/**
 * Tracker de progreso compatible con chip AppBar / ToolsEdgePanel (shape R2).
 *
 * @param {{
 *   totalTasks: number,
 *   seriesInfo: Array<{ seriesNumber?: number, description?: string, totalTasks?: number }>,
 *   loadSource?: string|(() => string),
 *   onProgress?: (stats: object) => void,
 *   throttleMs?: number,
 *   extraStats?: (() => object)|null,
 *   expectedTotal?: number,
 *   catalogComplete?: boolean,
 * }} opts
 */
export function createDownloadProgressTracker(opts) {
  let totalTasks = Math.max(0, Number(opts.totalTasks) || 0)
  let expectedTotal = Math.max(0, Number(opts.expectedTotal) || 0)
  if (expectedTotal > totalTasks) totalTasks = expectedTotal
  // R2 / local / Orthanc: catálogo ya cerrado. WADO progresivo pasa false.
  let catalogComplete = opts.catalogComplete !== false
  const seriesInfo = Array.isArray(opts.seriesInfo) ? opts.seriesInfo : []
  const onProgress = opts.onProgress
  const throttleMs =
    opts.throttleMs != null ? Math.max(50, Number(opts.throttleMs) || 220) : 220
  const extraStats =
    typeof opts.extraStats === 'function' ? opts.extraStats : null
  const resolveLoadSource =
    typeof opts.loadSource === 'function'
      ? opts.loadSource
      : () => opts.loadSource || 'r2'

  const tLoadStart = performance.now()
  let totalBytes = 0
  let totalDone = 0
  let loadedOk = 0
  let failed = 0
  const seriesBytes = new Map()
  const seriesDone = new Map()
  const seriesFirstCompleteAt = new Map()
  const seriesCompleteElapsedMs = new Map()
  const seriesWallDurationMs = new Map()
  let lastEmitT = tLoadStart
  let lastTotalBytes = 0
  let lastTotalDone = 0
  const lastSeriesBytesSnapshot = new Map()
  const lastSeriesDoneSnapshot = new Map()
  let lastProgressEmit = 0

  // Tiempo mínimo antes de mostrar métricas instantáneas (evitar "<0.1" en warmup)
  const WARMUP_MS = 1500

  function buildStats() {
    const now = performance.now()
    const elapsedMs = now - tLoadStart
    const elapsedSec = elapsedMs / 1000
    const deltaT = Math.max((now - lastEmitT) / 1000, 0.001)
    const deltaBytes = totalBytes - lastTotalBytes
    const deltaDone = Math.max(0, totalDone - lastTotalDone)
    const instantMiBps = deltaBytes / deltaT / (1024 * 1024)
    const avgMiBps = elapsedSec > 0 ? totalBytes / elapsedSec / (1024 * 1024) : 0
    // Durante warmup, usar promedio acumulado en lugar de instantáneo
    const useAvgForInstant = elapsedMs < WARMUP_MS && totalDone === 0
    const instantImgsPerSec = useAvgForInstant
      ? null
      : deltaDone / deltaT
    const avgImgsPerSec = elapsedSec > 0 ? totalDone / elapsedSec : 0

    const series = seriesInfo.map((info, idx) => {
      const done = seriesDone.get(idx) || 0
      const sb = seriesBytes.get(idx) || 0
      const prevS = lastSeriesBytesSnapshot.get(idx) || 0
      const prevDone = lastSeriesDoneSnapshot.get(idx) || 0
      const seriesDelta = sb - prevS
      const seriesDeltaDone = Math.max(0, done - prevDone)
      return {
        seriesNumber: info.seriesNumber,
        description: info.description,
        loaded: done,
        total: info.totalTasks,
        bytes: sb,
        instantMiBps: seriesDelta / deltaT / (1024 * 1024),
        instantImgsPerSec: seriesDeltaDone / deltaT,
        completeElapsedMs: seriesCompleteElapsedMs.has(idx)
          ? seriesCompleteElapsedMs.get(idx)
          : null,
        seriesWallMs: seriesWallDurationMs.has(idx)
          ? seriesWallDurationMs.get(idx)
          : null,
      }
    })

    lastEmitT = now
    lastTotalBytes = totalBytes
    lastTotalDone = totalDone
    seriesInfo.forEach((_, idx) => {
      lastSeriesBytesSnapshot.set(idx, seriesBytes.get(idx) || 0)
      lastSeriesDoneSnapshot.set(idx, seriesDone.get(idx) || 0)
    })

    const shownTotal = Math.max(totalTasks, expectedTotal)
    const base = {
      completed: totalDone,
      totalTasks: shownTotal,
      expectedTotal,
      catalogComplete,
      loadedOk,
      failed,
      elapsedMs,
      studyFullyLoaded:
        catalogComplete && shownTotal > 0 && totalDone >= shownTotal,
      totalBytes,
      instantMiBps,
      avgMiBps,
      instantImgsPerSec,
      avgImgsPerSec,
      series,
      loadSource: resolveLoadSource(),
    }

    if (extraStats) {
      try {
        return { ...base, ...extraStats() }
      } catch (e) {
        return base
      }
    }
    return base
  }

  function emit(force) {
    if (!onProgress) return
    const now = performance.now()
    const shownTotal = Math.max(totalTasks, expectedTotal)
    if (
      !force &&
      totalDone < shownTotal &&
      now - lastProgressEmit < throttleMs
    ) {
      return
    }
    lastProgressEmit = now
    try {
      onProgress(buildStats())
    } catch (e) {
      console.warn('downloadProgressTracker: onProgress', e)
    }
  }

  /**
   * @param {{ seriesIdx: number, ok?: boolean, bytes?: number }} result
   */
  function recordTaskDone(result) {
    const seriesIdx =
      result && typeof result.seriesIdx === 'number' ? result.seriesIdx : 0
    const ok = !!(result && result.ok)
    const bytes =
      result && typeof result.bytes === 'number' && result.bytes > 0
        ? result.bytes
        : 0

    totalDone += 1
    if (ok) {
      loadedOk += 1
      if (bytes > 0) {
        totalBytes += bytes
        seriesBytes.set(seriesIdx, (seriesBytes.get(seriesIdx) || 0) + bytes)
      }
    } else {
      failed += 1
    }

    const prevDone = seriesDone.get(seriesIdx) || 0
    seriesDone.set(seriesIdx, prevDone + 1)
    const doneCount = seriesDone.get(seriesIdx)
    const totalForSeries =
      (seriesInfo[seriesIdx] && seriesInfo[seriesIdx].totalTasks) || 0

    if (doneCount === 1) {
      seriesFirstCompleteAt.set(seriesIdx, performance.now())
    }
    if (totalForSeries > 0 && doneCount >= totalForSeries) {
      const tNow = performance.now()
      seriesCompleteElapsedMs.set(seriesIdx, tNow - tLoadStart)
      const firstAt = seriesFirstCompleteAt.get(seriesIdx)
      if (firstAt != null) {
        seriesWallDurationMs.set(seriesIdx, Math.max(0, tNow - firstAt))
      }
    }
  }

  function setExpectedTotal(n) {
    const v = Math.max(0, Math.floor(Number(n) || 0))
    if (v > expectedTotal) expectedTotal = v
    if (expectedTotal > totalTasks) totalTasks = expectedTotal
  }

  function growTotalTasks(n) {
    const v = Math.max(0, Math.floor(Number(n) || 0))
    if (v > totalTasks) totalTasks = v
  }

  function setCatalogComplete(done, knownTotal) {
    catalogComplete = done !== false
    if (!catalogComplete) return
    const known = Math.max(
      totalDone,
      Math.floor(Number(knownTotal) || 0),
      0
    )
    totalTasks = known
    expectedTotal = known
  }

  return {
    recordTaskDone,
    emit,
    buildStats,
    setExpectedTotal,
    growTotalTasks,
    setCatalogComplete,
    get totals() {
      return { totalDone, loadedOk, failed, totalBytes, totalTasks, expectedTotal }
    },
  }
}

/**
 * Estimar bytes de una imagen/blob cargado.
 * @param {object|null|undefined} image
 * @param {ArrayBuffer|Blob|null|undefined} raw
 * @returns {number}
 */
export function estimateLoadedBytes(image, raw) {
  if (image && typeof image.sizeInBytes === 'number' && image.sizeInBytes > 0) {
    return image.sizeInBytes
  }
  if (raw) {
    if (typeof raw.byteLength === 'number') return raw.byteLength
    if (typeof raw.size === 'number') return raw.size
  }
  return 0
}
